import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import {
  type ContactConsentSnapshot,
  contactConsentSnapshotSchema,
} from '@borradh-workspace/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type SetContactConsentInput,
  buildSetContactConsentPayload,
} from './contact-consent.payload';

interface ContactConsentScope {
  organizationId: string;
  actorId: string;
  leadId: string;
}

/** Always read fresh: a stale snapshot would be refused on save anyway. */
export const useContactConsent = (scope: ContactConsentScope) =>
  useQuery({
    queryKey: queryKeys.contactConsent.detail(
      scope.organizationId,
      scope.actorId,
      scope.leadId
    ),
    queryFn: () =>
      apiClient.get<ContactConsentSnapshot>(
        `leads/${scope.leadId}/contact-consent`,
        { schema: contactConsentSnapshotSchema }
      ),
    staleTime: 0,
    retry: false,
  });

export const useSetContactConsent = (
  scope: ContactConsentScope,
  options: { onError?: () => void } = {}
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetContactConsentInput) =>
      apiClient.put<ContactConsentSnapshot>(
        `leads/${scope.leadId}/contact-consent`,
        buildSetContactConsentPayload(input),
        { schema: contactConsentSnapshotSchema }
      ),
    onSuccess: (value) => {
      queryClient.setQueryData(
        queryKeys.contactConsent.detail(
          scope.organizationId,
          scope.actorId,
          scope.leadId
        ),
        value
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.leads.detail(scope.leadId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.all(),
      });
    },
    onError: () => options.onError?.(),
  });
};
